import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, Calendar } from "lucide-react";
import { useEffect } from "react";

const blogPosts = [
  {
    slug: "creative-username-ideas-2025",
    title: "500+ Creative Username Ideas for 2025: Stand Out on Social Media",
    description: "Discover the ultimate guide to creating memorable usernames for Instagram, TikTok, YouTube, and more. Expert tips and hundreds of examples included.",
    date: "2025-01-15",
    readTime: "8 min read",
    category: "Username Tips"
  },
  {
    slug: "social-media-branding-guide",
    title: "Complete Social Media Branding Guide: Build Your Online Identity",
    description: "Learn how to create a cohesive brand across all social platforms. From color schemes to voice consistency, master the art of digital branding.",
    date: "2025-01-10",
    readTime: "10 min read",
    category: "Branding"
  },
  {
    slug: "perfect-bio-writing-tips",
    title: "How to Write the Perfect Bio: 15 Proven Tips That Convert Followers",
    description: "Master the art of bio writing with our comprehensive guide. Includes templates, examples, and strategies for Instagram, Twitter, LinkedIn, and TikTok.",
    date: "2025-01-05",
    readTime: "7 min read",
    category: "Bio Writing"
  }
];

const Blog = () => {
  useEffect(() => {
    document.title = "NameBioLab Blog - Username & Bio Tips, Social Media Branding Guides";
    
    const structuredData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Blog",
          "@id": "https://namebiolab.lovable.app/blog#blog",
          "url": "https://namebiolab.lovable.app/blog",
          "name": "NameBioLab Blog",
          "description": "Expert tips, guides, and strategies for creating standout usernames, writing engaging bios, and building your social media brand.",
          "blogPost": blogPosts.map(post => ({
            "@type": "BlogPosting",
            "@id": `https://namebiolab.lovable.app/blog/${post.slug}#article`,
            "headline": post.title,
            "description": post.description,
            "datePublished": post.date,
            "url": `https://namebiolab.lovable.app/blog/${post.slug}`
          }))
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://namebiolab.lovable.app/blog#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://namebiolab.lovable.app/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Blog",
              "item": "https://namebiolab.lovable.app/blog"
            }
          ]
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </Link>

        <header className="mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">NameBioLab Blog</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Expert tips, guides, and strategies for creating standout usernames, writing engaging bios, 
            and building your social media brand.
          </p>
        </header>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {blogPosts.map((post) => (
            <Card key={post.slug} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                  <span className="bg-primary/10 text-primary px-2 py-1 rounded-md text-xs font-medium">
                    {post.category}
                  </span>
                  <span>•</span>
                  <span>{post.readTime}</span>
                </div>
                <CardTitle className="text-xl mb-2 line-clamp-2">{post.title}</CardTitle>
                <CardDescription className="line-clamp-3">{post.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    {new Date(post.date).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric' 
                    })}
                  </div>
                  <Link to={`/blog/${post.slug}`}>
                    <Button variant="ghost" size="sm">
                      Read More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <section className="mt-16 bg-card rounded-2xl p-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Need Username or Bio Ideas Right Now?</h2>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Don't wait! Use our free AI-powered generator to create unique usernames and engaging bios instantly.
          </p>
          <Link to="/">
            <Button size="lg" className="bg-gradient-to-r from-primary to-secondary">
              Try Free Generator
            </Button>
          </Link>
        </section>
      </div>
    </div>
  );
};

export default Blog;
